import { createPortal } from 'react-dom';
import BookingStatusScreen from './BookingStatusScreen';
import { useAuthStatus } from '../context/AuthContext';
import { spinnerRing } from '../utils/bookingUtils';
import '../css/TrackBookingModal.css';

/**
 * "Track Booking" popup opened from the guest header's nav button — shows
 * the same search/status UI as the standalone /bookings[:bookingId] page
 * (see BookingStatusScreen's `embedded` mode) without navigating away from
 * whatever screen the guest is currently on (landing page or mid-booking
 * flow). The standalone route itself is untouched and still needed for the
 * confirmation email's shareable tracking link.
 *
 * Portalled to document.body — same reasoning as EquipmentRentalModal:
 * this can be opened while the guest is behind App.jsx's own
 * .screen-modal-overlay (a fixed, overflow-y:auto container), and nesting
 * another position:fixed overlay inside that is unreliable across browsers.
 *
 * @param {boolean} isOpen
 * @param {() => void} onClose
 */
function TrackBookingModal({ isOpen, onClose }) {
  // The landing page can render before the visitor's sign-in finishes, so
  // this may open while the database is still off-limits. The lookup screen
  // mounts only once signed in; until then the card shows a spinner.
  const { ready: authReady } = useAuthStatus();

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return createPortal(
    <div className="tbm-overlay" onClick={handleBackdropClick}>
      <div className="tbm-card">
        <button type="button" className="tbm-close" onClick={onClose} aria-label="Close">&times;</button>
        {authReady
          ? <BookingStatusScreen embedded onClose={onClose} />
          : <div className="tbm-loading">{spinnerRing('Connecting…')}</div>}
      </div>
    </div>,
    document.body
  );
}

export default TrackBookingModal;
